import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Center, Group, Loader, Modal, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { createTask, deleteTask, getClients, getProjects, getTaskAssignees, getTasks, updateTask } from "../api";
import FilterBar from "../components/FilterBar";
import TaskList from "./TaskList";
import TaskAgenda from "./TaskAgenda";
import TaskEditModal from "./TaskEditModal";
import { toUpdateDto } from "./taskDto";

// True when the task's due date is today or earlier — both compared as UTC calendar days to match
// the backend's My Day definition exactly (DueDate < UtcNow.Date + 1), so the optimistic list, the
// counts and the server agree in every timezone.
function dueByToday(task) {
  if (!task.dueDate) return false;
  const d = new Date(task.dueDate);
  const dueKey = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const todayKey = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return dueKey <= todayKey;
}

// Whether a task still belongs in the current smart list after a change (so a completed task drops
// out, a no-longer-due task leaves "My Day", an un-starred task leaves "Important", etc.). Panels
// pass scope=null and keep everything (open + completed shown together).
function belongsToScope(task, scope) {
  if (!scope) return true;
  if (scope === "completed") return task.isCompleted;
  if (task.isCompleted) return false;
  if (scope === "important") return task.isImportant;
  if (scope === "myDay") return dueByToday(task); // My Day = overdue + due today
  if (scope === "planned") return !!task.dueDate;
  return true; // assigned, all
}

/**
 * The Tasks workhorse: loads a task list (by smart-list scope or by linked record), renders it with
 * optimistic inline toggles serialized per task, and hosts the create/edit modal + delete confirm.
 */
const PAGE_SIZE = 50;

export default function TasksView({ fetchParams, presetLink = null, showLinks = false, emptyMessage, onChanged }) {
  const { t } = useTranslation("Veft");
  const scope = fetchParams?.scope ?? null;
  // On a project's own task panel the project tag is redundant on every row, so it's hidden there.
  const hideProjectTag = !!presetLink?.projectId;

  // The all/completed scopes are tenant-wide team views; the other smart lists are pinned to the
  // current user. Only the team views take the person filter (elsewhere it would fight the pin).
  const teamScope = scope === "all" || scope === "completed";

  // Hub-only filters (compose with the smart list on the server).
  const [projectFilter, setProjectFilter] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);
  const effectiveParams = {
    ...(fetchParams ?? {}),
    ...(projectFilter ? { projectId: projectFilter } : {}),
    ...(teamScope && assigneeFilter ? { assigneeUserId: assigneeFilter } : {}),
  };
  const paramsKey = JSON.stringify(effectiveParams);

  // Pagination — only the Completed list is paged server-side; other scopes return their full set.
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [tasks, setTasks] = useState(null);
  const tasksRef = useRef([]);
  const inflightRef = useRef({});
  const [userOptions, setUserOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectClientMap, setProjectClientMap] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("agenda"); // agenda is the default view in the hub

  // The agenda groups by due date, which only makes sense for open scopes — the Completed list
  // always renders flat. Offered in the hub only (showLinks).
  const showViewToggle = showLinks && scope !== "completed";
  const agenda = showViewToggle && viewMode === "agenda";

  useEffect(() => { tasksRef.current = tasks ?? []; }, [tasks]);

  const reload = useCallback(
    () => getTasks({ ...effectiveParams, page: 0, pageSize: PAGE_SIZE })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setTasks(list);
        setPage(0);
        // Only the Completed list is paged; a full page means there may be more.
        setHasMore(scope === "completed" && list.length === PAGE_SIZE);
      })
      .catch(() => { showNotification({ color: "red", title: t("tasks_load_failed") }); setTasks([]); setHasMore(false); }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch keyed by serialized params
    [paramsKey],
  );

  useEffect(() => { setTasks(null); reload(); }, [reload]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await getTasks({ ...effectiveParams, page: nextPage, pageSize: PAGE_SIZE });
      const list = Array.isArray(res) ? res : [];
      setTasks((prev) => [...(prev ?? []), ...list]);
      setPage(nextPage);
      setHasMore(list.length === PAGE_SIZE);
    } catch {
      showNotification({ color: "red", title: t("tasks_load_failed") });
    } finally {
      setLoadingMore(false);
    }
  };

  // Assignee options (always); project/client link-picker options (hub only).
  useEffect(() => {
    getTaskAssignees()
      .then((rows) => setUserOptions((rows ?? []).map((u) => ({ value: u.id, label: u.name }))))
      .catch(() => {});
    if (showLinks) {
      getProjects().then((p) => {
        const list = p ?? [];
        setProjectOptions(list.map((x) => ({ value: x.id, label: x.title })));
        setProjectClientMap(Object.fromEntries(list.filter((x) => x.clientId).map((x) => [x.id, x.clientId])));
      }).catch(() => {});
      getClients().then((c) => setClientOptions((c ?? []).map((x) => ({ value: x.id, label: x.name })))).catch(() => {});
    }
  }, [showLinks]);

  // A task belongs in the current view when it matches BOTH the smart-list scope and (in the hub)
  // the active project filter. Optimistic create/edit/toggle use this so a row that no longer matches
  // the filter drops out immediately instead of lingering until the next server reload.
  const matchesView = (task) =>
    belongsToScope(task, scope)
    && (!projectFilter || task.projectId === projectFilter)
    && (!(teamScope && assigneeFilter) || task.assigneeUserId === assigneeFilter);

  // Optimistic field mutation (inline toggle) via a full PUT, serialized per task so rapid edits
  // land in order and the row leaves the list if it no longer matches the scope.
  const mutate = (task, overrides) => {
    const next = { ...task, ...overrides };
    setTasks((prev) => {
      const list = (prev ?? []).map((x) => (x.id === task.id ? next : x));
      return matchesView(next) ? list : list.filter((x) => x.id !== task.id);
    });
    const send = async () => {
      const current = tasksRef.current.find((x) => x.id === task.id) ?? next;
      try {
        await updateTask(task.id, toUpdateDto(current));
        onChanged?.();
      } catch (e) {
        showNotification({ color: "red", title: t("tasks_save_failed"), message: "" + e });
        reload();
      }
    };
    const prev = inflightRef.current[task.id] ?? Promise.resolve();
    inflightRef.current[task.id] = prev.then(send, send);
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateTask(editing.id, toUpdateDto(editing, values));
        setTasks((prev) => {
          const list = (prev ?? []).map((x) => (x.id === updated.id ? updated : x));
          return matchesView(updated) ? list : list.filter((x) => x.id !== updated.id);
        });
      } else {
        const created = await createTask({ ...values, isCompleted: false, ...(presetLink ?? {}) });
        if (matchesView(created)) setTasks((prev) => [created, ...(prev ?? [])]);
      }
      setModalOpened(false);
      onChanged?.();
    } catch (e) {
      showNotification({ color: "red", title: t("tasks_save_failed"), message: "" + e });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTask(deleting.id);
      setTasks((prev) => (prev ?? []).filter((x) => x.id !== deleting.id));
      onChanged?.();
    } catch (e) {
      showNotification({ color: "red", title: t("tasks_delete_failed"), message: "" + e });
    } finally {
      setDeleting(null);
    }
  };

  if (tasks === null) return <Center py="xl"><Loader /></Center>;

  const rowHandlers = {
    onToggleComplete: (task) => mutate(task, { isCompleted: !task.isCompleted }),
    onToggleImportant: (task) => mutate(task, { isImportant: !task.isImportant }),
    onEdit: (task) => { setEditing(task); setModalOpened(true); },
    onDelete: setDeleting,
  };

  return (
    <Stack>
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        {(showViewToggle || showLinks) ? (
          <Box style={{ flex: 1, minWidth: 0 }}>
            <FilterBar activeCount={[projectFilter, teamScope && assigneeFilter].filter(Boolean).length}>
              {showViewToggle && (
                <SegmentedControl
                  size="xs"
                  value={viewMode}
                  onChange={setViewMode}
                  data={[
                    { label: t("tasks_view_list"), value: "list" },
                    { label: t("tasks_view_agenda"), value: "agenda" },
                  ]}
                />
              )}
              {showLinks && (
                <Select
                  size="xs"
                  placeholder={t("tasks_filter_project")}
                  data={projectOptions}
                  value={projectFilter}
                  onChange={setProjectFilter}
                  clearable
                  searchable
                  w={210}
                />
              )}
              {showLinks && teamScope && (
                <Select
                  size="xs"
                  placeholder={t("tasks_filter_assignee")}
                  data={userOptions}
                  value={assigneeFilter}
                  onChange={setAssigneeFilter}
                  clearable
                  searchable
                  w={190}
                />
              )}
            </FilterBar>
          </Box>
        ) : <div />}
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); setModalOpened(true); }} style={{ flexShrink: 0 }}>
          {t("tasks_add")}
        </Button>
      </Group>

      {agenda ? (
        <TaskAgenda tasks={tasks} emptyMessage={emptyMessage ?? t("tasks_empty")} hideProjectTag={hideProjectTag} {...rowHandlers} />
      ) : (
        <TaskList tasks={tasks} emptyMessage={emptyMessage ?? t("tasks_empty")} hideProjectTag={hideProjectTag} {...rowHandlers} />
      )}

      {hasMore && (
        <Center>
          <Button variant="subtle" size="xs" loading={loadingMore} onClick={loadMore}>
            {t("tasks_load_more")}
          </Button>
        </Center>
      )}

      <TaskEditModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={onSave}
        saving={saving}
        initial={editing}
        userOptions={userOptions}
        projectOptions={projectOptions}
        projectClientMap={projectClientMap}
        clientOptions={clientOptions}
        showLinks={showLinks}
      />

      <Modal opened={!!deleting} onClose={() => setDeleting(null)} title={t("tasks_delete")}>
        <Stack>
          <Text>{t("tasks_delete_confirm")}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleting(null)}>{t("cancel", "Cancel")}</Button>
            <Button color="red" onClick={confirmDelete}>{t("delete", "Delete")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
